// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';

const WhiteboardCanvas = ({
    canvasRef,
    backgroundImage,
    activeTool,
    color,
    thickness,
    setToolbarExpanded,
    onColorUsed,
}) => {
    const canvasId = useRef(`whiteboard-canvas-${Date.now()}`).current;
    const previousSizeRef = useRef({ width: 0, height: 0 });
    const [selectedTextObj, setSelectedTextObj] = useState(null);

    // resizes the canvas and scales all objects.
    const doScaleAndResize = () => {
        const imgEl = document.getElementById('main-illustration-img');
        if (!imgEl) return;

        const rect = imgEl.getBoundingClientRect();

        const newWidth = rect.width;
        const newHeight = rect.height;

        if (newWidth < 1 || newHeight < 1) {
            console.warn('Skipping resize: newWidth or newHeight is zero.', newWidth, newHeight);
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!previousSizeRef.current.width || !previousSizeRef.current.height) {
            canvas.setWidth(newWidth);
            canvas.setHeight(newHeight);
            previousSizeRef.current = { width: newWidth, height: newHeight };
            canvas.renderAll();
            return;
        }

        const { width: oldW, height: oldH } = previousSizeRef.current;

        const scaleX = newWidth / oldW;
        const scaleY = newHeight / oldH;
        canvas.getObjects().forEach((obj) => {
            obj.scaleX *= scaleX;
            obj.scaleY *= scaleY;
            obj.left *= scaleX;
            obj.top *= scaleY;
            obj.setCoords();
        });
        canvas.setWidth(newWidth);
        canvas.setHeight(newHeight);
        previousSizeRef.current = { width: newWidth, height: newHeight };
        canvas.renderAll();
    };

    function getCursorForTool(tool) {
        switch (tool) {
            case 'eraser':
                return 'not-allowed';
            case 'pencil':
            case 'highlighter':
            case 'spray':
                return 'crosshair';
            case 'text':
                return 'text';
            default:
                return 'default';
        }
    }

    function updateCursor(tool, canvas) {
        const cursor = getCursorForTool(tool);
        canvas.defaultCursor = cursor;
        canvas.hoverCursor = cursor;
    }

    useEffect(() => {
        const canvasElement = document.getElementById(canvasId);
        canvasRef.current = new fabric.Canvas(canvasElement, { isDrawingMode: false });
        // Make canvas focusable (helps some iOS focus edge cases)
        canvasRef.current.upperCanvasEl.setAttribute('tabindex', '0');

        canvasRef.current.defaultCursor = getCursorForTool(activeTool);

        // Expose doScaleAndResize + a pointer to previousSizeRef
        canvasRef.current.doScaleAndResize = doScaleAndResize;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const handleResize = () => {
            doScaleAndResize();
            // delayed re-check in case of very fast/sudden switch to different device
            setTimeout(() => {
                doScaleAndResize();
            }, 300);
        };

        window.addEventListener('resize', handleResize);
        // Run initial call:
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            canvas.dispose();
            canvasRef.current = null;
        };
    }, [canvasId, canvasRef]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // console.warn('[WhiteboardCanvas] setToolbarExpanded exists?', !!setToolbarExpanded);

        canvas.off('mouse:down');
        canvas.setToolbarExpanded = setToolbarExpanded;

        if (activeTool !== 'text') {
            setSelectedTextObj(null); // Hide icon when switching tool
        }

        if (activeTool === 'eraser') {
            canvas.isDrawingMode = false;
            canvas.on('mouse:down', (evt) => {
                if (evt.target) {
                    canvas.remove(evt.target);
                    canvas.renderAll();
                }
            });
            canvas.selection = false; // prevent box selection
            canvas.getObjects().forEach((obj) => (obj.selectable = false));
        } else if (activeTool === 'text') {
            canvas.isDrawingMode = false;

            let suppressIconOnce = false;

            function showIconFor(obj) {
                if (!obj || obj.type !== 'i-text' || obj.isEditing) {
                    setSelectedTextObj(null);
                    return;
                }

                obj.setCoords();
                setSelectedTextObj(obj);
            }

            // Selection changes
            canvas.on('selection:created', (e) => {
                const obj = e.selected?.[0];
                if (suppressIconOnce || obj?.isEditing) {
                    suppressIconOnce = false;
                    return;
                }
                if (obj?.type === 'i-text') setSelectedTextObj(obj);
            });

            canvas.on('selection:updated', (e) => showIconFor(e.selected?.[0]));
            canvas.on('selection:cleared', () => {
                setSelectedTextObj(null);
            });

            // Enter / exit edit-mode
            canvas.on('text:editing:exited', (e) => {
                e.target.set({ editable: false, selectable: true, hasControls: true });
                showIconFor(e.target);
            });

            canvas.on('mouse:down', (evt) => {
                const active = canvas.getActiveObject?.();
                if (active && active.type === 'i-text' && active.isEditing) {
                    return; // ignore empty taps while editing
                }

                const target = evt.target;

                if (target && target.type === 'i-text') {
                    canvas.setActiveObject(target);
                    // Only select – don't enter editing immediately
                    return;
                }

                setSelectedTextObj(null);
                suppressIconOnce = true;

                const p = canvas.getPointer(evt.e);
                const txt = new fabric.IText('New Text', {
                    left: p.x,
                    top: p.y,
                    fill: color,
                    fontSize: (thickness / 4) * 20,
                    fontFamily: 'Arial',
                    selectable: true,
                    editable: true,
                });
                canvas.add(txt);
                canvas.setActiveObject(txt);

                const isTouch = !!(
                    evt.e?.pointerType === 'touch' ||
                    /^touch/i.test(evt.e?.type) ||
                    ('ontouchstart' in window && !evt.e?.pointerType)
                );

                const startEditing = () => {
                    txt.set({ editable: true });
                    txt.enterEditing();
                    txt.selectAll();
                    focusHiddenTextarea(txt);
                    canvas.requestRenderAll();
                };

                if (isTouch) {
                    // iOS needs focus in the same gesture to open the keyboard
                    startEditing();
                } else {
                    // Desktop/mouse: defer one frame to avoid any layout/focus steals
                    requestAnimationFrame(startEditing);
                }
            });

            // Keyboard: clean setup  teardown; auto-enter edit for text
            const keydownHandler = (e) => {
                const active = canvas.getActiveObject();
                // If a text object is selected but not editing, capture keys and start editing
                if (active && active.type === 'i-text' && !active.isEditing) {
                    if (!e.metaKey && !e.ctrlKey && !e.altKey) e.preventDefault(); // stop page scroll/search
                    active.set({ editable: true });
                    active.enterEditing();
                    active.hiddenTextarea?.focus();
                    canvas.requestRenderAll();
                    return; // first keystroke is consumed; subsequent keys go to the editor
                }
                // Delete selected (not editing)
                if ((e.key === 'Backspace' || e.key === 'Delete') && active && !active.isEditing) {
                    e.preventDefault();
                    canvas.remove(active);
                    canvas.requestRenderAll();
                }
                // Escape: clear selection
                if (e.key === 'Escape') {
                    setSelectedTextObj(null);
                    canvas.discardActiveObject();
                    canvas.requestRenderAll();
                }
            };
            window.addEventListener('keydown', keydownHandler);
            // remove on tool change / unmount
            return () => window.removeEventListener('keydown', keydownHandler);
        } else {
            canvas.isDrawingMode = true;
            if (activeTool === 'spray') {
                canvas.freeDrawingBrush = new fabric.SprayBrush(canvas);
                canvas.freeDrawingBrush.color = color;
            } else if (activeTool === 'highlighter') {
                canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
                canvas.freeDrawingBrush.color = color;
                canvas.freeDrawingBrush.opacity = 1;
            } else if (activeTool === 'pencil') {
                canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
                canvas.freeDrawingBrush.color = color;
                canvas.freeDrawingBrush.opacity = 1;
            }
            canvas.freeDrawingBrush.width = thickness;

            // When a freehand path is actually created, record the colour (skip highlighter)
            canvas.off('path:created'); // avoid stacking listeners
            canvas.on('path:created', () => {
                if (['pencil', 'spray'].includes(activeTool)) {
                    onColorUsed?.(color, activeTool);
                }
            });
        }
        if (activeTool !== 'text') {
            canvas.on('mouse:down', () => {
                if (canvas.setToolbarExpanded && activeTool !== 'eraser') {
                    canvas.setToolbarExpanded(true);
                }
            });
        }

        updateCursor(activeTool, canvas);
        canvas.renderAll();
    }, [activeTool, color, thickness, canvasRef]);

    function focusHiddenTextarea(iTextObj) {
        const ta = iTextObj?.hiddenTextarea;
        if (!ta) return;
        ta.style.fontSize = '16px';
        ta.style.webkitTextSizeAdjust = '100%';
        ta.style.textSizeAdjust = '100%';
        ta.setAttribute('inputmode', 'text');
        ta.setAttribute('autocapitalize', 'sentences');
        ta.setAttribute('autocomplete', 'off');
        ta.setAttribute('autocorrect', 'on');
        ta.focus({ preventScroll: true });
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <canvas
                id={canvasId}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'auto',
                    zIndex: 2,
                    cursor: getCursorForTool(activeTool),
                }}
            />
            {selectedTextObj?.type === 'i-text' && !selectedTextObj.isEditing && (
                <IconButton
                    onClick={() => {
                        if (!selectedTextObj) return;
                        selectedTextObj.set({ editable: true });
                        selectedTextObj.enterEditing();
                        selectedTextObj.selectAll();
                        focusHiddenTextarea(selectedTextObj);
                        canvasRef.current.renderAll();
                    }}
                    style={{
                        position: 'absolute',
                        right: '12px',
                        bottom: '12px',
                        zIndex: 10,
                        backgroundColor: '#FF669B',
                        border: 'none',
                        padding: '6px',
                        color: 'white',
                    }}
                    size="medium"
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            )}
        </div>
    );
};

export default WhiteboardCanvas;
